import type { TabsSchemaType } from '@vedalib/editor/lib/brand'
import {
  PositionTabsElementType,
  SCHEMA,
  SizeTabsElementType,
  ViewTabsElementType,
} from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useCallback, useMemo } from 'react'
import React from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import type { IFieldConfig } from 'components/formKit'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldNumberArray,
  FieldRadio,
  FieldSegmented,
  FieldSelect,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import { TileButton } from 'components/uiKit/Button'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { IDENTITY } from 'constants/commonConstans'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS, OFFSET_ARRAY } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'
import { getEnumOption } from 'utils/enum'

import indentContent from './assets/indent_content.svg'
import indentTabs from './assets/indent_tabs.svg'
import type { TabsFormBrandType } from './tabsTypes'

const ALIGN_TEXT_OPTIONS = [
  { value: 'start' as const, label: <Icon name='alignmentAlignLeft' size={KitSize.S} /> },
  { value: 'center' as const, label: <Icon name='alignmentAlignMid' size={KitSize.S} /> },
  { value: 'end' as const, label: <Icon name='alignmentAlignRight' size={KitSize.S} /> },
]

const useShowTabsSolid = ({ name, getter = IDENTITY }: IFieldConfig) => {
  const values = useFormData<TabsSchemaType>(name, 2)
  const hidden = getter(values?.view?.type) !== ViewTabsElementType.solid
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowTabsSplitted = ({ name, getter = IDENTITY }: IFieldConfig) => {
  const values = useFormData<TabsSchemaType>(name, 2)
  const hidden = getter(values?.view?.type) !== ViewTabsElementType.splitted
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowDivider = ({ name, getter = IDENTITY }: IFieldConfig) => {
  const values = useFormData<TabsSchemaType>(name, 2)
  const hidden = getter(values?.splitter?.borderStyle) === 'none'
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowTabSize = ({ name, getter = IDENTITY }: IFieldConfig) => {
  const values = useFormData<TabsSchemaType>(name, 2)
  const hidden = getter(values?.tabs?.tabSize) === SizeTabsElementType.hug
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowIcons = ({ name, getter = IDENTITY }: IFieldConfig) => {
  const values = useFormData<TabsSchemaType>(name, 2)
  const hidden = !getter(values?.icons?.show)
  return useMemo(() => ({ hidden }), [hidden])
}

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.tabs.style, {
  view: {
    type: genBrandField({
      as: FieldSelect<ViewTabsElementType>,
      label: t('input.label.type'),
      props: {
        options: [
          getEnumOption('ViewTabsElementType', ViewTabsElementType.solid),
          getEnumOption('ViewTabsElementType', ViewTabsElementType.splitted),
        ],
      },
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter, useShowTabsSolid],
    }),
  },
  border: {
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter, useShowTabsSolid],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder, useShowTabsSolid],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder, useShowTabsSolid],
    }),
  },
  tabs: {
    position: genBrandField({
      as: FieldSegmented<PositionTabsElementType>,
      label: t('input.label.position'),
      layout: 'vertical',
      props: {
        fluid: true,
        options: [
          getEnumOption('PositionTabsElementType', PositionTabsElementType.top),
          getEnumOption('PositionTabsElementType', PositionTabsElementType.left),
          getEnumOption('PositionTabsElementType', PositionTabsElementType.right),
        ],
      },
    }),
    tabSize: genBrandField({
      as: FieldSelect<SizeTabsElementType>,
      label: t('input.label.tabSize'),
      props: {
        options: [
          getEnumOption('SizeTabsElementType', SizeTabsElementType.fixed),
          getEnumOption('SizeTabsElementType', SizeTabsElementType.hug),
        ],
      },
    }),
    width: genBrandField({
      as: FieldNumber,
      label: t('input.label.width'),
      rules: [{ max: 760, message: ' ' }],
      props: { width: 64 },
      useConfigs: [useShowTabSize],
    }),
    textAlign: genBrandField({
      as: FieldRadio<'start' | 'center' | 'end'>,
      label: t('input.label.alignText'),
      props: {
        hideRadio: true,
        direction: 'horizontal',
        options: ALIGN_TEXT_OPTIONS,
        fluid: false,
      },
      useConfigs: [useShowTabSize],
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  activeTab: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.tabs.tags.selectTab'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  borderTab: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter, useShowTabsSplitted],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter, useShowTabsSplitted],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder, useShowTabsSplitted],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder, useShowTabsSplitted],
    }),
  },
  splitter: {
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter, useShowTabsSolid],
      label: t('input.label.showDivider'),
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      useConfigs: [useBrandColorOptions, useShowBorder, useShowTabsSolid, useShowDivider],
      label: t('input.label.dividerColor'),
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder, useShowTabsSolid, useShowDivider],
    }),
  },
  content: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter, useShowTabsSplitted],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter, useShowTabsSplitted],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder, useShowTabsSplitted],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder, useShowTabsSplitted],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.tabs.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  content_effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.tabs.schema.contentBoxShadow'),
      useConfigs: [useThemeValueGetter, useShowTabsSplitted],
    }),
  },
  icons: {
    show: genBrandField({
      as: FieldSegmented<boolean>,
      label: t('elements.tabs.tags.icon.showButton'),
      info: t('elements.tabs.tags.icon.description'),
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions, useShowIcons],
    }),
  },
  iconsHover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.tabs.schema.hoverColor'),
      useConfigs: [useBrandColorOptions, useShowIcons],
      props: { transparent: true, labeled: true, resettable: true },
    }),
  },
  iconsColor: {
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.tabs.schema.iconColor'),
      useConfigs: [useBrandColorOptions, useShowIcons],
    }),
  },
  iconLeft: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.tabs.schema.prev'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false /* resettable: true */ },
      useConfigs: [useShowIcons],
    }),
  },
  iconRight: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.tabs.schema.next'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false /* resettable: true */ },
      useConfigs: [useShowIcons],
    }),
  },
  indents: {
    padding: genBrandField({
      as: FieldNumberArray,
      label: t('elements.tabs.schema.activeTabPaddings'),
      layout: 'vertical',
      description: <img alt='tab indents' src={indentTabs} style={{ width: 272 }} />,
      props: { options: OFFSET_ARRAY },
    }),
    margin: genBrandField({
      as: FieldNumberArray,
      props: { options: OFFSET_ARRAY },
      label: t('elements.tabs.schema.contentPaddings'),
      layout: 'vertical',
      description: <img alt='content indents' src={indentContent} style={{ width: 272 }} />,
    }),
  },
})

const TabsStyleForm: TabsFormBrandType = ({ device, subConfigs, edited, setForm }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom bottomSpace gap sides>
        <FormField config={schema.view.type} />
        <FormField config={schema.view.borderRadius} />
        <FormField config={schema.border.borderStyle} />
        <FormField config={schema.border.borderColor} />
        <FormField config={schema.border.borderWidth} />

        <Typography weight='medium'>{t('elements.tabs.tags.tabs')}</Typography>
        <FormField config={schema.tabs.position} />
        <FormField config={schema.tabs.tabSize} />
        <FormField config={schema.tabs.width} />
        <FormField config={schema.tabs.textAlign} />
        <FormField config={schema.tabs.backgroundColor} />
        <FormField config={schema.hover.backgroundColor} />
        <FormField config={schema.activeTab.backgroundColor} />
        <FormField config={schema.borderTab.borderRadius} />
        <FormField config={schema.borderTab.borderStyle} />
        <FormField config={schema.borderTab.borderColor} />
        <FormField config={schema.borderTab.borderWidth} />
        <FormField config={schema.splitter.borderStyle} />
        <FormField config={schema.splitter.borderColor} />
        <FormField config={schema.splitter.borderWidth} />

        <Typography weight='medium'>{t('elements.tabs.tags.content')}</Typography>
        <FormField config={schema.content.backgroundColor} />
        <FormField config={schema.content.borderRadius} />
        <FormField config={schema.content.borderStyle} />
        <FormField config={schema.content.borderColor} />
        <FormField config={schema.content.borderWidth} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />
        <FormField config={schema.content_effects.boxShadow} />

        <Typography weight='medium'>{t('elements.tabs.tags.icon')}</Typography>
        <FormField config={schema.icons.show} />
        <FormField config={schema.icons.backgroundColor} />
        <FormField config={schema.iconsHover.backgroundColor} />
        <FormField config={schema.iconsColor.color} />
        <FormField config={schema.iconLeft.backgroundImage} />
        <FormField config={schema.iconRight.backgroundImage} />

        <Typography weight='medium'>{t('elements.tabs.tags.indents')}</Typography>
        <FormField config={schema.indents.padding} />
        <FormField config={schema.indents.margin} />

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(TabsStyleForm) as typeof TabsStyleForm
